// Used in login-routing.module.ts

// Angular
import { Component, OnDestroy, OnInit } from "@angular/core";
import { UntypedFormBuilder, Validators } from "@angular/forms";
import { Router } from "@angular/router";

// RXJS/Store
import * as RouterStore from "#core/router-store";
import * as LoginStore from "#login/store";
import { Store, select } from "@ngrx/store";
import { Observable, Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

// Services/Types/Helpers
import { FormHelper } from "#core/helpers/form.helper";
import { LocalStorage } from "#core/services/local-storage.service";

// Validators
import { EmailValidator } from "#validators/email.validator";
import { Auth, RegistrationDistrictTokenResponse } from "@carnegie-learning/data-access-angular-passport";
import { School } from "@carnegie-learning/data-access-fido";

@Component({
    selector: "login-onboard-district-teachers",
    templateUrl: "./login-onboard-district-teachers.component.html",
    styleUrls: ["./login-onboard-district-teachers.component.scss"],
    standalone: false
})
export class LoginOnboardDistrictTeachersComponent
    implements OnInit, OnDestroy
{
    registrationForm = this.fb.group(
        {
            school: [
                null,
                {
                    validators: [Validators.required],
                    updateOn: "change",
                },
            ],
            email: [
                "",
                {
                    validators: [Validators.required, EmailValidator.email],
                    updateOn: "change",
                },
            ],
            inviteHash: [{ value: "", disabled: true }],
        },
        { updateOn: "change" }
    );
    maintenanceMode: boolean = (<any>window).maintenanceMode;
    bannerStatus$?: Observable<boolean>;
    bannerMessage$?: Observable<string>;
    bannerColor$?: Observable<string>;
    showProgressBar$?: Observable<boolean>;
    authorized = false;
    tokenPayload?: RegistrationDistrictTokenResponse;
    districtInviteHash = "";

    private ngUnsubscribe = new Subject();

    constructor(
        private router: Router,
        public loginStore: Store<LoginStore.UserState>,
        public routerStore: Store<RouterStore.State>,
        public auth: Auth,
        private fb: UntypedFormBuilder,
        private formHelper: FormHelper,
        private localStorage: LocalStorage
    ) {}
    ngOnInit() {
        this.bannerStatus$ = this.loginStore.pipe(
            select(LoginStore.getRegisterBannerStatus)
        );
        this.bannerMessage$ = this.loginStore.pipe(
            select(LoginStore.getRegisterBannerMessage)
        );
        this.bannerColor$ = this.loginStore.pipe(
            select(LoginStore.getRegisterBannerColor)
        );
        this.showProgressBar$ = this.loginStore.pipe(
            select(LoginStore.getRegisterProgressBar)
        );
        this.loginStore
            .pipe(select(LoginStore.getAuthorized))
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((authorized: boolean) => {
                this.authorized = authorized;
            });

        this.loginStore
            .pipe(select(LoginStore.getDistrictRegistrationPayload))
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((payload: RegistrationDistrictTokenResponse | null) => {
                if (payload?.schools.length) {
                    this.tokenPayload = payload;
                    this.registrationForm.patchValue({
                        inviteHash: this.districtInviteHash,
                    });
                }
            });

        if (this.maintenanceMode) {
            this.loginStore.dispatch(LoginStore.registerMaintenance());
        } else {
            // Clear out local storage and reset state
            this.localStorage.clear();
            this.loginStore.dispatch(LoginStore.userProfileResetState());
            this.loginStore.dispatch(LoginStore.registerResetState());

            this.routerStore
                .pipe(select(RouterStore.getRouterState))
                .pipe(takeUntil(this.ngUnsubscribe))
                .subscribe((router) => {
                    if (
                        router &&
                        router.state.url.includes("onboard-district/form")
                    ) {
                        this.districtInviteHash =
                            router.state.queryParams["invite-hash"];

                        // Verify invite hash returns a valid district with schools
                        if (
                            this.districtInviteHash === undefined ||
                            this.districtInviteHash === null ||
                            this.districtInviteHash === "null" ||
                            this.districtInviteHash === "undefined"
                        ) {
                            this.loginStore.dispatch(
                                LoginStore.registerDistrictNoToken()
                            );
                        } else {
                            // Token found, need to verify token is valid and schools exist
                            this.loginStore.dispatch(
                                LoginStore.registerDistrictTokenCheckAttempt({
                                    inviteHash: this.districtInviteHash,
                                })
                            );
                        }
                    }
                });
        }
    }

    ngOnDestroy() {
        this.ngUnsubscribe.next(true);
        this.ngUnsubscribe.complete();
    }

    goToSupportForm() {
        return this.router.navigate(["support"]);
    }

    goToLoginForm() {
        return this.router.navigate(["login/form"]);
    }

    validate(field: string, type: string) {
        return this.formHelper.Validate(this.registrationForm, field, type);
    }

    submit() {
        this.registrationForm.markAsUntouched();
    }

    trackBySchool(index: number, school: School) {
        return school.schoolid;
    }
}
